import {
  IconSend,
  IconHome,
  IconMap,
  IconChartLine,
  IconSettings,
  IconLifebuoy,
  IconLogout,
  IconPencil,
  IconArchive,
  IconX,
  IconWallet,
  IconFolderOpen,
  IconCalendar,
  IconFileCheck,
  IconEye,
  IconCircleCheck,
  IconDownload,
  IconArrowUp,
  IconPercentage,
  IconArrowUpRight,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconAlertTriangle,
  IconCalculator,
  IconSearch,
  IconFileUpload,
  IconTrash,
  IconEdit,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconExternalLink,
  IconCoins,
  IconUserSquare,
  IconShieldCheck,
  IconInfoCircle,
  IconMailExclamation,
  IconAdjustmentsPlus,
  IconFilterShare,
  IconEyeSearch,
  IconBriefcase,
  IconHomeExclamation,
  IconReportMoney,
  IconCreditCard,
  IconHeartHandshake,
  IconChartBar,
  IconClipboard,
  IconDeviceFloppy,
  IconReceipt,
  IconCalendarCheck,
  IconDatabase,
} from '@tabler/icons-react';
import { ComponentType, HTMLAttributes, SVGProps } from 'react';

interface IconWrapperProps extends HTMLAttributes<HTMLOrSVGElement> {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
}

function IconWrapper({
  Icon, className, ...props
}: Readonly<IconWrapperProps>) {
  // Set width/height to 1em for font-size scaling
  return <Icon className={className} {...props} strokeWidth={2} />;
}

const customIcons = {
  admin: <IconUserSquare width="1em" height="1em" strokeWidth={2} />,
  archiveIcon: <IconArchive width="1em" height="1em" strokeWidth={2} />,
  arrowUpRightIcon: <IconArrowUpRight width="1em" height="1em" strokeWidth={2} />,
  barChart: <IconChartBar width="1em" height="1em" strokeWidth={2} />,
  briefcase: <IconBriefcase width="1em" height="1em" strokeWidth={1.5} />,
  calendarIcon: <IconCalendar width="1em" height="1em" strokeWidth={2} />,
  calendarCheckIcon: <IconCalendarCheck width="1em" height="1em" strokeWidth={2} />,
  calculatorIcon: <IconCalculator width="1em" height="1em" strokeWidth={2} />,
  checkCircleIcon: <IconCircleCheck width="1em" height="1em" strokeWidth={2} />,
  chevronDown: <IconChevronDown width="1em" height="1em" strokeWidth={2} />,
  chevronLeft: <IconChevronLeft width="1em" height="1em" strokeWidth={2} />,
  chevronRight: <IconChevronRight width="1em" height="1em" strokeWidth={2} />,
  chevronUp: <IconChevronUp width="1em" height="1em" strokeWidth={2} />,
  clipboard: <IconClipboard width="1em" height="1em" strokeWidth={2} />,
  clipboardMoney: <IconReportMoney width="1em" height="1em" strokeWidth={1.5} />,
  closeIcon: <IconX width="1em" height="1em" strokeWidth={2} />,
  coins: <IconCoins width="1em" height="1em" strokeWidth={2} />,
  creditCard: <IconCreditCard width="1em" height="1em" strokeWidth={1.5} />,
  databaseIcon: <IconDatabase width="1em" height="1em" strokeWidth={2} />,
  dashIcon: <IconMinus width="1em" height="1em" strokeWidth={2} />,
  downloadIcon: <IconDownload width="1em" height="1em" strokeWidth={2} />,
  editIcon: <IconPencil width="1em" height="1em" strokeWidth={2} />,
  editLineIcon: <IconEdit width="1em" height="1em" strokeWidth={2} />,
  errorIcon: <IconAlertTriangle width="1em" height="1em" strokeWidth={2} />,
  externalLink: <IconExternalLink width="1em" height="1em" strokeWidth={2} />,
  eyeSearch: <IconEyeSearch width="1em" height="1em" strokeWidth={1.5} />,
  fileCheck2Icon: <IconFileCheck width="1em" height="1em" strokeWidth={2} />,
  fileUp: <IconFileUpload width="1em" height="1em" strokeWidth={2} />,
  folderOpenIcon: <IconFolderOpen width="1em" height="1em" strokeWidth={2} />,
  homeIcon: <IconHome width="1em" height="1em" strokeWidth={2} />,
  homeExclamation: <IconHomeExclamation width="1em" height="1em" strokeWidth={1.5} />,
  heartHandshake: <IconHeartHandshake width="1em" height="1em" strokeWidth={1.5} />,
  infoIcon: <IconInfoCircle width="1em" height="1em" strokeWidth={2} />,
  lineChartIcon: <IconChartLine width="1em" height="1em" strokeWidth={2} />,
  logoutIcon: <IconLogout width="1em" height="1em" strokeWidth={2} />,
  mailWarningIcon: <IconMailExclamation width="1em" height="1em" strokeWidth={2} />,
  mapIcon: <IconMap width="1em" height="1em" strokeWidth={2} />,
  adjustmentsPlusIcon: <IconAdjustmentsPlus width="1em" height="1em" strokeWidth={2} />,
  percentIcon: <IconPercentage width="1em" height="1em" strokeWidth={2} />,
  previewIcon: <IconEye width="1em" height="1em" strokeWidth={2} />,
  receiptIcon: <IconReceipt width="1em" height="1em" strokeWidth={2} />,
  save: <IconDeviceFloppy width="1em" height="1em" strokeWidth={2} />,
  searchIcon: <IconSearch width="1em" height="1em" strokeWidth={2} />,
  sendIcon: <IconSend width="1em" height="1em" strokeWidth={2} />,
  settingsIcon: <IconSettings width="1em" height="1em" strokeWidth={2} />,
  shieldCheckIcon: <IconShieldCheck width="1em" height="1em" strokeWidth={2} />,
  supportIcon: <IconLifebuoy width="1em" height="1em" strokeWidth={2} />,
  syncIcon: <IconFilterShare width="1em" height="1em" strokeWidth={2} />,
  trashIcon: <IconTrash width="1em" height="1em" strokeWidth={2} />,
  trendingUpIcon: <IconTrendingUp width="1em" height="1em" strokeWidth={2} />,
  trendingDownIcon: <IconTrendingDown width="1em" height="1em" strokeWidth={2} />,
  walletIcon: <IconWallet width="1em" height="1em" strokeWidth={2} />,
};

export function getCustomIcon(iconKey: string) {
  if (iconKey in customIcons) {
    return customIcons[iconKey as keyof typeof customIcons];
  }
  return undefined;
}

export const dynamicIcons = {
  // eslint-disable-next-line react/jsx-props-no-spreading
  arrowUpIcon: (props: any) => <IconWrapper Icon={IconArrowUp} {...props} />,
};

export default customIcons;
